import {
	Environment,
	RecordSource,
	RelayFeatureFlags,
	type EnvironmentConfig,
	type IEnvironment,
} from 'relay-runtime';
// @ts-ignore
import LiveResolverStore from 'relay-runtime/lib/store/live-resolvers/LiveResolverStore';
import { defaultDataIdFromObject } from 'apollo-cache-inmemory';

import RelayMissingFieldHandlers from '@atlassian/relay-missing-field-handlers';
import RelayScheduler from '@atlassian/relay-scheduler';

import { network, noopNetwork } from '@confluence/relay-network/entry-points/network.ts';

// Enable Relay Resolvers globally
RelayFeatureFlags.ENABLE_RELAY_RESOLVERS = true;

export type RelayEnvironmentOptions = {
	disableNetwork?: boolean;
};

const defaultRelayEnvironmentOptions: RelayEnvironmentOptions = {
	disableNetwork: false,
};

const getConfluenceDataID = (fieldValue: { [key: string]: unknown }, typeName: string) => {
	if (typeName === 'WebItem') {
		return `${fieldValue.completeKey || fieldValue.id}; ${fieldValue.url}`;
	} else if (typeName === 'OnboardingState') {
		return `${fieldValue.key}`;
	} else if (typeName === 'WebSection') {
		return `${fieldValue.cacheKey}` || `${String(fieldValue.id)}`;
	} else if (typeName === 'DetailsSummaryLines') {
		return `${fieldValue.macroId}_${fieldValue.currentPage}`;
	} else if (typeName === 'DetailLine') {
		return `${fieldValue.id}_${fieldValue.rowId}_${fieldValue.macroId}`;
	} else if (typeName === 'ConfluenceSystemTemplate') {
		return `${fieldValue.moduleCompleteKey}`;
	}

	// Ideally, we would use  RelayDataID from platform/packages/relay/relay-data-id here.
	// But this is to avoid breaking changes to cache reads via readFragment usages which specify id as a string.
	return defaultDataIdFromObject(fieldValue);
};

/**
 * Provides the relay environment config for Confluence.
 * This inlines the logic previously provided by the deprecated getDefaultRelayEnvironmentConfig
 * from @atlassian/relay-environment-config, with Confluence-specific overrides applied.
 *
 * @returns The relay environment config for Confluence.
 */
export function getConfluenceRelayEnvironmentConfig(
	relayEnvironmentOptions: RelayEnvironmentOptions = defaultRelayEnvironmentOptions,
): EnvironmentConfig {
	const recordSource = new RecordSource(window.__RELAY_STORE__);
	return {
		configName: 'Atlassian Graphql Gateway - Confluence',
		network: relayEnvironmentOptions.disableNetwork ? noopNetwork : network,
		store: new LiveResolverStore(recordSource, {
			getDataID: getConfluenceDataID,
		}),
		getDataID: getConfluenceDataID,
		scheduler: RelayScheduler,
		missingFieldHandlers: RelayMissingFieldHandlers,
	};
}

/**
 * Gets the relay environment for Confluence. This function is exported to allow the relay environment to be used in tests.
 *
 * @returns The relay environment for Confluence.
 */

let relayEnvironment: IEnvironment | null = null;
export function getRelayEnvironment(
	relayEnvironmentOptions: RelayEnvironmentOptions = defaultRelayEnvironmentOptions,
): IEnvironment {
	if (window['GLOBAL_RELAY_ENV']) {
		return window['GLOBAL_RELAY_ENV'];
	}
	if (!relayEnvironment) {
		relayEnvironment = new Environment(
			getConfluenceRelayEnvironmentConfig(relayEnvironmentOptions),
		);
	}
	window['GLOBAL_RELAY_ENV'] = relayEnvironment;
	return relayEnvironment;
}

export function resetRelayEnvironment() {
	relayEnvironment = null;
	window['GLOBAL_RELAY_ENV'] = undefined;
}
